import React from 'react';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { lineAmount, money } from './totals';
import { SectionHeading, AddRowButton, DOC_FIELD } from './DocumentChrome';

/**
 * The priced rows, as the sheet prints them.
 *
 * Amount is never typed: it is quantity × rate and it moves while the rate is
 * being typed, which is the number the person quoting is actually watching. The
 * serial column is the row's position, so deleting row 2 renumbers the rest
 * without anything having to renumber them.
 */
export default function LineItems({ heading, items, onChange, onAdd, onRemove }) {
  return (
    <div>
      <SectionHeading>{heading}</SectionHeading>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] border-collapse" data-testid="line-items-table">
          <thead>
            <tr className="bg-doc-navy text-[9px] font-semibold uppercase tracking-[0.08em] text-white">
              <th scope="col" className="w-8 px-2 py-1.5 text-center">#</th>
              <th scope="col" className="px-2 py-1.5 text-left">Description</th>
              <th scope="col" className="w-20 px-2 py-1.5 text-left">HSN Code</th>
              <th scope="col" className="w-24 px-2 py-1.5 text-right">Quantity</th>
              <th scope="col" className="w-16 px-2 py-1.5 text-left">Unit</th>
              <th scope="col" className="w-28 px-2 py-1.5 text-right">Rate (₹)</th>
              <th scope="col" className="w-28 px-2 py-1.5 text-right">Amount (₹)</th>
              <th scope="col" className="w-8 px-1 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-2 py-4 text-center text-xs text-slate-400">
                  No rows yet — add the first one below
                </td>
              </tr>
            ) : items.map((item, index) => (
              <tr key={item.id} className="border-b border-slate-100 even:bg-slate-50/70" data-testid={`line-item-${index}`}>
                <td className="px-2 text-center font-mono text-[11px] text-doc-muted">{index + 1}</td>
                <td className="px-1">
                  <Input
                    value={item.description ?? ''}
                    onChange={(e) => onChange(item.id, { description: e.target.value })}
                    placeholder="Label / item description"
                    aria-label={`Row ${index + 1} description`}
                    data-testid={`line-description-${index}`}
                    className={DOC_FIELD}
                  />
                </td>
                <td className="px-1">
                  <Input
                    value={item.hsn_code ?? ''}
                    onChange={(e) => onChange(item.id, { hsn_code: e.target.value })}
                    placeholder="4911"
                    aria-label={`Row ${index + 1} HSN code`}
                    className={DOC_FIELD}
                  />
                </td>
                <td className="px-1">
                  <Input
                    type="number"
                    value={item.quantity ?? ''}
                    onChange={(e) => onChange(item.id, { quantity: e.target.value })}
                    placeholder="0"
                    aria-label={`Row ${index + 1} quantity`}
                    data-testid={`line-quantity-${index}`}
                    className={`${DOC_FIELD} text-right font-mono tabular-nums`}
                  />
                </td>
                <td className="px-1">
                  <Input
                    value={item.unit ?? ''}
                    onChange={(e) => onChange(item.id, { unit: e.target.value })}
                    placeholder="pcs"
                    aria-label={`Row ${index + 1} unit`}
                    className={DOC_FIELD}
                  />
                </td>
                <td className="px-1">
                  <Input
                    type="number"
                    value={item.rate ?? ''}
                    onChange={(e) => onChange(item.id, { rate: e.target.value })}
                    placeholder="0.00"
                    aria-label={`Row ${index + 1} rate`}
                    data-testid={`line-rate-${index}`}
                    className={`${DOC_FIELD} text-right font-mono tabular-nums`}
                  />
                </td>
                <td className="px-2 text-right font-mono text-[12px] font-semibold tabular-nums text-doc-navy"
                  data-testid={`line-amount-${index}`}>
                  {money(lineAmount(item))}
                </td>
                <td className="px-1 text-center">
                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    aria-label={`Delete row ${index + 1}`}
                    data-testid={`delete-line-${index}`}
                    className="rounded p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-1.5">
        <AddRowButton onClick={onAdd} testId="add-line-item">+ Add Row</AddRowButton>
      </div>
    </div>
  );
}
